import { defineComponent } from "@vue/composition-api";
import { debounce, throttle } from "lodash-es";
import { CreateElement, VNode } from "vue";
import { BlockingStatus } from "@scrile/api-provider/dist/api/BlockingStatusProvider";
import { MessageThread, MessageThreadsSearchResult } from "@scrile/api-provider/dist/api/MessageThreadsProvider";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthUser from "@/hooks/useAuthUser";
import { useGroups } from "@/hooks/useGroups";
import useSessionStorage, { USER_SETTINGS } from "@/hooks/useSessionStorage";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import config from "@/config";
import { EVENTS } from "@/const";
import MessageThreadsView from "./components/MessageThreadsView.vue";
import { DEFAULT_SORT, getMessageThreadsSortData } from "./config";

export default defineComponent({
  name: "PageMessageThreads",
  components: { MessageThreadsView },
  setup() {
    const { authorities: authUserAuthorities, authUserStrict, isCreator } = useAuthUser();
    const { groups, getData: getGroupsData } = useGroups({ system: true });
    const { getUserSetting, setUserSetting } = useSessionStorage();
    return {
      authUserAuthorities,
      authUserStrict,
      groups,
      isCreator,
      getGroupsData,
      getUserSetting,
      setUserSetting,
    };
  },
  data() {
    return {
      scroll: false,
      loading: false,
      messageThreadsSearchResult: null as MessageThreadsSearchResult | null,
      blockingStatus: [] as BlockingStatus[],
      userMessageSetting: {} as Record<string, any>,
      term: "",
      filterLoading: false,
    };
  },
  computed: {
    allowMassMessage(): boolean {
      return this.hasMoreThanOneMembersInGroups;
    },
    allowSortThreads(): boolean {
      return config.featureSortMessages;
    },
    messageThreadList(): MessageThread[] {
      return this.messageThreadsSearchResult?.result ?? [];
    },
    authUserId(): string {
      return this.authUserStrict.id;
    },
    hasMoreThanOneMembersInGroups(): boolean {
      return this.groups.some(i => i.memberCount > 1);
    },
    sortItemsArray() {
      if (this.allowSortThreads) return getMessageThreadsSortData().sortItems;
      return [DEFAULT_SORT];
    },
  },
  methods: {
    async getGroups() {
      if (checkAuthorities(this.authUserAuthorities, ["updateMyContentPrice"])) this.getGroupsData();
    },
    updateThread(thread: MessageThread) {
      if (!this.messageThreadsSearchResult) return;
      this.messageThreadsSearchResult = {
        ...this.messageThreadsSearchResult,
        result: this.messageThreadsSearchResult.result.map(i => (i.id === thread.id ? thread : i)),
      };
    },
    async getThreads(page = 0, replace = true, includeDeleted = false) {
      this.scroll = false;
      const sortData = {
        ...this.sortItemsArray[(this.allowSortThreads && this.userMessageSetting?.sort) || 0],
      };
      try {
        const response = await providers.MessageThreadsProvider.find({
          data: {
            userId: this.authUserStrict.id,
            page,
            includeDeleted,
            size: 20,
            term: this.term || undefined,
            ...sortData,
          },
        });
        await this.getBlockingStatusList(response.result);
        this.messageThreadsSearchResult = {
          ...response,
          result:
            replace || !this.messageThreadsSearchResult
              ? response.result
              : this.messageThreadsSearchResult.result.concat(response.result),
        };
      } finally {
        this.scroll = this.messageThreadsSearchResult?.hasNextPage ?? false;
        this.filterLoading = false;
      }
    },
    async getBlockingStatusList(messageThreads: MessageThread[]) {
      if (messageThreads.length === 0 || !messageThreads) {
        return;
      }

      const opponentIds = messageThreads.map(thread =>
        thread.userIds[0] === this.authUserId ? thread.userIds[1] : thread.userIds[0]
      );
      const result = await providers.BlockingStatusProvider.searchByOpponents({ data: { opponentIds } });
      this.blockingStatus = this.blockingStatus.concat(result);
    },
    async getPageData() {
      try {
        this.loading = true;
        this.getUserMessageSetting();
        await Promise.all([this.getThreads(), this.getGroups()]);
      } finally {
        this.loading = false;
      }
    },

    getUserMessageSetting() {
      this.userMessageSetting = this.getUserSetting(USER_SETTINGS.MESSAGE_SETTINGS);
    },
    saveUserMessageSetting(newSettings: Record<string, any>) {
      this.userMessageSetting = { ...this.userMessageSetting, ...newSettings };
      this.setUserSetting(USER_SETTINGS.MESSAGE_SETTINGS, this.userMessageSetting);
    },
    onMessage() {
      this.getThreads();
    },
    setTerm(term: string) {
      this.term = term;
      this.filterLoading = true;
      this.filterByTerm();
    },
    filterByTerm() {
      this.getThreads();
    },
    getNextPage(cb: Function) {
      const nextPage = (this.messageThreadsSearchResult?.page ?? 0) + 1;
      this.getThreads(nextPage, false).finally(() => cb());
    },
  },
  render(h: CreateElement): VNode {
    return h(MessageThreadsView, {
      props: {
        loading: this.loading,
        authUserId: this.authUserId,
        allowMassMessage: this.allowMassMessage,
        messageThreadList: this.messageThreadList,
        blockingStatus: this.blockingStatus,
        scroll: this.scroll,
        currentSortItem: this.userMessageSetting?.sort || 0,
        filterLoading: this.filterLoading,
        term: this.term,
      },
      on: {
        sort: (select: number) => {
          this.saveUserMessageSetting({ sort: select });
          this.getThreads(0, true);
        },
        massMessage: () => this.$router.push({ name: "massMessage" }),
        scheduledMassMessage: () => this.$router.push({ name: "scheduledMassMessages" }),
        next: this.getNextPage,
        filter: this.setTerm,
        clearFilter: () => this.setTerm(""),
      },
    });
  },
  created() {
    this.onMessage = throttle(this.onMessage, 3000, { trailing: false });
    this.filterByTerm = debounce(this.filterByTerm, 500);
    this.getPageData();
    emitter.on<any>(EVENTS.MESSAGES_NEW_MESSAGE, this.onMessage);
    emitter.on<any>(EVENTS.MESSAGES_MARKED_READ, this.onMessage);
    emitter.on<any>(EVENTS.MESSAGES_UPDATE_THREAD, this.updateThread);
  },
  beforeDestroy() {
    emitter.off<any>(EVENTS.MESSAGES_NEW_MESSAGE, this.onMessage);
    emitter.off<any>(EVENTS.MESSAGES_MARKED_READ, this.onMessage);
    emitter.off<any>(EVENTS.MESSAGES_UPDATE_THREAD, this.updateThread);
  },
});
